import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import ReactMarkdown from "react-markdown";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  FileText,
  ShieldCheck,
} from "lucide-react";
import { CascadeTrail } from "./CascadeTrail";
import { Feedback } from "./Feedback";
import { VisualCard } from "./VisualCard";
import { PhotoViewer } from "./VisualViewer";

// `core/sources.py` dagi `kind` -> yorliq
const SOURCE_KIND = {
  nomenklatura: "Nomenklatura",
  izoh: "Izoh",
  qoida: "Qoida",
  tushuntirish: "Tushuntirish",
  hujjat: "Hujjat",
};

const time = (at) =>
  new Date(at || Date.now()).toLocaleTimeString("uz-UZ", {
    hour: "2-digit",
    minute: "2-digit",
  });

function CopyButton({ text, className = "" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1600);
      }}
      title="Nusxa olish"
      className={`rounded p-1 text-sky-300 transition-colors hover:bg-sky-800/50 ${className}`}
    >
      {done ? (
        <Check className="h-3.5 w-3.5 text-emerald-400" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

/** Xabar — foydalanuvchi savoli, tayyor javob yoki fotodan tasvir. */
export function Message({ message, onRetryVisual, onDrawVisual }) {
  if (message.role === "visual") {
    return <VisualCard message={message} onRetry={onRetryVisual} onDraw={onDrawVisual} />;
  }
  return message.role === "user" ? (
    <UserMessage message={message} />
  ) : (
    <BotMessage message={message} />
  );
}

function UserMessage({ message }) {
  const { text, photos = [], full = [], at } = message;
  const [open, setOpen] = useState(null); // ochilgan foto tartibi
  const [broken, setBroken] = useState([]); // saqlash muddati o'tib o'chirilgan fotolar
  const shown = photos.map((src, i) => [src, i]).filter(([, i]) => !broken.includes(i));
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="mb-6 pt-2"
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-sky-400/80">
          Siz
        </span>
        <span className="font-mono text-[10px] text-sky-500/40">{time(at)}</span>
      </div>
      {shown.length > 0 && (
        <div className="mb-2 flex gap-2">
          {shown.map(([src, i]) => (
            <button
              key={src}
              type="button"
              onClick={() => setOpen(i)}
              title="Fotoni kattalashtirish"
              className="overflow-hidden rounded-xl ring-sky-400/70 transition hover:ring-2 focus-visible:ring-2"
            >
              <img
                src={src}
                alt=""
                onError={() => setBroken((b) => [...b, i])}
                className="h-24 w-24 cursor-zoom-in object-cover sm:h-28 sm:w-28"
              />
            </button>
          ))}
        </div>
      )}
      {open !== null && (
        <PhotoViewer
          photos={shown.map(([src, i]) => full[i] || src)}
          thumbs={shown.map(([src]) => src)}
          index={Math.max(0, shown.findIndex(([, i]) => i === open))}
          onClose={() => setOpen(null)}
        />
      )}
      {text && (
        <div className="text-lg font-bold leading-snug tracking-tight text-white sm:text-xl">
          {text}
        </div>
      )}
    </motion.div>
  );
}

function BotMessage({ message }) {
  const [showSources, setShowSources] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    text, code, level, ms, turnId, rated,
    levels = [], sources = [], warning, done, at,
  } = message;

  // Javob tugamaguncha nomzodlar/manbalar chizilmaydi — yarim
  // ma'lumot ko'rsatish chalg'itadi.
  const settled = done;

  const exportText = () => {
    const blob = new Blob(
      [`AIEXPERT — EKSPERTIZA XULOSASI\n${new Date(at).toLocaleString("uz-UZ")}\n\n${text}`],
      { type: "text/plain;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aiexpert-xulosa-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="relative mb-8"
    >
      <div className="flex items-start gap-3 sm:gap-3.5">
        <div className="hidden shrink-0 flex-col items-center self-stretch pt-1 sm:flex">
          <div className="h-2 w-2 rounded-full bg-sky-400 shadow-sm shadow-sky-400/50" />
          <div className="my-1 w-0.5 flex-1 bg-gradient-to-b from-sky-500/50 via-sky-800/30 to-transparent" />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2.5">
              <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-sky-400/40 bg-sky-950/80 p-0.5">
                <img src="/logo.png" alt="" className="h-full w-full object-contain" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold tracking-wide text-white sm:text-sm">
                  AIExpert
                </span>
                <span className="rounded border border-sky-800/60 bg-sky-950 px-1.5 py-0.5 text-[10px] font-semibold text-sky-300">
                  Ekspert xulosasi
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 font-mono text-[11px] text-sky-400/60">
              {ms > 0 && (
                <span className="flex items-center gap-1 rounded border border-sky-900/50 bg-[#031329]/60 px-1.5 py-0.5 text-sky-300/80">
                  <Clock className="h-3 w-3 text-sky-400" />
                  {(ms / 1000).toFixed(1)}s
                </span>
              )}
              <span>{time(at)}</span>
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-sky-500/25 bg-gradient-to-b from-[#061e42]/90 to-[#04142d]/95 p-4 shadow-xl shadow-sky-950/40 sm:p-6">
            {/*
              === KELAJAKDA: boj va QQS stavkalari ===

              Maketda bu yerda "Import boji 0%", "QQS 12%" bloklari bor
              edi. Ular OLIB TASHLANDI, chunki bazada bunday ustun yo'q
              (`nomenclature`: code, title_uz, title_ru, level, ...).
              Maketdagi raqamlar qo'lda yozilgan namuna edi.

              Bojxona masalasida yolg'on stavka jarima demakdir — shuning
              uchun "taxminan" ko'rsatish mumkin emas. Tarif ma'lumoti
              bazaga qo'shilgach shu blok ochiladi:

              {rates && (
                <div className="grid grid-cols-2 gap-2 border-b border-sky-800/30 pb-3 sm:grid-cols-4">
                  <Rate icon={Percent} label="Import boji" value={rates.duty} />
                  <Rate icon={TrendingUp} label="QQS" value={rates.vat} />
                </div>
              )}
            */}

            {settled && levels.length > 0 && <CascadeTrail levels={levels} ms={ms} />}

            {settled && code && levels.length === 0 && (
              <div className="flex items-center gap-2">
                <span className="rounded bg-sky-950/80 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-sky-400">
                  {level}x
                </span>
                <span className="font-mono text-base font-bold tracking-wide text-white">
                  {code}
                </span>
                <CopyButton text={code} />
              </div>
            )}

            <div className="md min-w-0 text-xs leading-relaxed text-slate-100 sm:text-sm">
              <ReactMarkdown>{text}</ReactMarkdown>
            </div>

            {/*
              Manbalar — javob QAYSI rasmiy matnga tayangani.
              Iqtibossiz javobni foydalanuvchi tekshira olmaydi.
            */}
            {settled && sources.length > 0 && (
              <div className="pt-1">
                <button
                  onClick={() => setShowSources((v) => !v)}
                  className="flex select-none items-center gap-1.5 py-1 text-xs font-medium text-sky-300 transition-colors hover:text-white"
                >
                  {showSources ? (
                    <ChevronUp className="h-4 w-4 text-sky-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-sky-400" />
                  )}
                  <FileText className="h-3.5 w-3.5 text-sky-400" />
                  <span>Manbalar ({sources.length})</span>
                </button>

                <AnimatePresence>
                  {showSources && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-2 overflow-hidden pt-2"
                    >
                      {sources.map((s, i) => (
                        <div
                          key={i}
                          className="rounded-xl border border-sky-800/40 bg-[#030f24]/90 p-2.5"
                        >
                          <div className="mb-1 flex items-start justify-between gap-2">
                            {/*
                              `cite` uzun bo'lishi mumkin (butun guruh
                              sarlavhasi). Shuning uchun monospace EMAS —
                              u katta harfli kirill matnini o'qib
                              bo'lmaydigan qiladi.
                            */}
                            <span className="min-w-0 text-[11px] font-semibold leading-snug text-sky-300">
                              {s.cite}
                            </span>
                            {(s.kind || s.authority) && (
                              <span className="shrink-0 rounded border border-sky-800/50 bg-sky-950/80 px-1.5 py-0.5 text-[10px] text-sky-400/80">
                                {SOURCE_KIND[s.kind] || s.authority}
                              </span>
                            )}
                          </div>
                          <p className="line-clamp-4 text-[11px] leading-relaxed text-slate-300/90">
                            {s.text}
                          </p>
                          {s.doc && (
                            <div className="mt-1 truncate text-[10px] text-sky-400/50">
                              {s.doc}
                            </div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {warning && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-xl border border-amber-700/40 bg-amber-950/30 p-2.5 text-[11px] text-amber-200"
              >
                <span className="shrink-0 text-amber-400">⚠</span>
                <span>{warning}</span>
              </div>
            )}

            {settled && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-sky-800/40 pt-3 text-xs">
                <div className="flex items-center gap-1.5 text-[11px] text-sky-300/70">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                  <span>Rasmiy TIF TN ma'lumotlari asosida</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(text);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1600);
                    }}
                    className="flex items-center gap-1 rounded-lg border border-sky-800/40 bg-[#041228] px-2.5 py-1 text-[11px] text-sky-200 transition-colors hover:bg-sky-900/60"
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    <span>{copied ? "Nusxalandi" : "Nusxa olish"}</span>
                  </button>

                  <button
                    onClick={exportText}
                    className="flex items-center gap-1 rounded-lg border border-sky-800/40 bg-[#041228] px-2.5 py-1 text-[11px] text-sky-200 transition-colors hover:bg-sky-900/60"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Yuklab olish</span>
                  </button>
                </div>
              </div>
            )}

            {/*
              Ekspert bahosi. Alohida qatorda — nusxa/yuklab olish
              tugmalari bilan aralashsa ko'z uni yana bir "amal"
              deb o'qiydi, holbuki bu O'LCHOV.
            */}
            {settled && (
              <div className="border-t border-sky-800/40 pt-3">
                <Feedback turnId={turnId} code={code} rated={rated} />
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
